import * as React from 'react';
import { Oval } from 'react-loader-spinner';
import { CSS, styled, VariantProps } from 'styles';
import color from 'styles/color';
import typography from 'styles/typography';

const StyledButton = styled('button', {
  display: 'flex',
  alignItems: 'center',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:disabled': {
    cursor: 'not-allowed',
  },
  variants: {
    grow: {
      true: {
        width: '100%',
      },
      false: {
        width: 'fit-content',
      },
    },
    variant: {
      primary: {
        backgroundColor: '$primary',
        color: '$white',
        boxShadow: '$shadow2',
        '&:active:enabled': {
          backgroundColor: '$primary2',
        },
        '&:hover:enabled': {
          backgroundColor: '$primary2',
        },
        '&:focus': {
          outlineColor: '#DCD4FF',
          outlineStyle: 'solid',
          outlineWidth: 'medium',
        },
        '&:disabled': {
          color: '$typeLightGrey',
          backgroundColor: '$typeDisabled2',
        },
      },
      secondary: {
        backgroundColor: '$white',
        color: '$primary',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '$primary2',
        boxShadow: '$shadow2',
        '&:hover:enabled': {
          backgroundColor: '#DCE9FC',
        },
        '&:active:enabled': {
          backgroundColor: '#DCE9FC',
        },
        '&:focus': {
          outlineColor: '#DCD4FF',
          outlineStyle: 'solid',
          outlineWidth: 'medium',
        },
        '&:disabled': {
          color: '$typeLightGrey',
          backgroundColor: '$neutral1',
          borderColor: '$neutral2',
        },
      },
      tertiary: {
        background: '$white',
        color: '$primary2',
        '&:hover:enabled': {
          backgroundColor: '#DCE9FC',
        },
        '&:active:enabled': {
          backgroundColor: '#DCE9FC',
        },
        '&:focus': {
          outlineColor: '#DCD4FF',
          outlineStyle: 'solid',
          outlineWidth: 'medium',
        },
        '&:disabled': {
          color: '$typeLightGrey',
        },
      },
      disabled: {
        color: '$typeLightGrey',
        backgroundColor: '$typeDisabled2',
        cursor: 'not-allowed',
      },
    },
    size: {
      large: {
        ...typography.buttonMedium,
        height: 48,
        px: 16,
        py: 10,
        svg: {
          width: 24,
          height: 24,
        },
      },
      medium: {
        ...typography.buttonSmall,
        height: 44,
        px: 16,
        py: 12,
        svg: {
          width: 18,
          height: 18,
        },
      },
      small: {
        ...typography.buttonExtraSmall,
        height: 28,
        px: 12,
        py: 6,
        svg: {
          width: 16,
          height: 16,
        },
      },
    },
    justify: {
      between: {
        justifyContent: 'space-between',
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    grow: false,
    size: 'large',
  },
});

const StyledSpan = styled('span', {});

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  onRenderLeft?: React.ReactNode;
  onRenderRight?: React.ReactNode;
  isLoading?: boolean;
};
type CssProps = { css?: CSS };
type ButtonVariants = VariantProps<typeof StyledButton>;

export type IButtonProps = ButtonProps & CssProps & ButtonVariants;

const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
  (props, ref) => {
    const {
      type = 'button',
      onRenderLeft: _onRenderLeft,
      onRenderRight,
      children,
      isLoading,
      ...rest
    } = props;

    const onRenderLeft = React.useCallback(() => {
      if (isLoading) {
        return <Oval color={color.primary} secondaryColor={color.primary5} />;
      }
      return _onRenderLeft;
    }, [_onRenderLeft, isLoading]);

    return (
      <StyledButton ref={ref} type={type} {...rest}>
        {onRenderLeft()}
        <StyledSpan
          css={{
            ml: onRenderLeft() ? 8 : 0,
            mr: onRenderRight ? 8 : 0,
          }}
        >
          {children}
        </StyledSpan>
        {onRenderRight}
      </StyledButton>
    );
  },
);

Button.displayName = 'Button';

export default Button;
